/** VisualizerInterface.js
 * @fileoverview VisualizerInterface: a JS class for a general 3D visualization interface.
 */

/* eslint-disable-file no-unused-vars */

import { Polymer, Residue } from './model/atomgroups';
import { getFullResidueId } from './util/mol_info_utils';

/** EventBroker event: the backend's NativeCommandState() changed (payload: the new state). */
export const NativeCommandStateEvent = 'nativeCommandState';
/**
 * EventBroker events around a live switch of the 3D viewer (display_mgr.switchVisualizerBackend),
 * for components that keep canvas objects redrawScene() does not re-create (payload
 * {from, to} backend names): on 'visualizerSwitching' remove them while the old visualizer is
 * still active; on 'visualizerSwitched' draw them again in the new one.
 */
export const VisualizerSwitchingEvent = 'visualizerSwitching';
export const VisualizerSwitchedEvent = 'visualizerSwitched';
/**
 * EventBroker event: a backend that loads asynchronously could not start (payload
 * {backend: backendName, error: string}). display_mgr falls back to 3Dmol when that backend is
 * the active one.
 */
export const VisualizerLoadFailedEvent = 'visualizerLoadFailed';

export class VisualizerInterface {
    /**
     * Short identifier of the rendering backend (e.g. '3dmol'). Used to select a backend
     * with the `?visualizer=` URL parameter, to namespace saved camera positions, and as the
     * `visualizer` attribute on the canvas wrapper element.
     */
    static get backendName() { return 'none'; }

    /**
     * Can this backend run in this browser? Asked before one is constructed (startup selection,
     * Preferences -> 3D Viewer). `requirement` names what is missing when it cannot.
     */
    static isSupported() { return true; }

    static get requirement() { return null; }

    constructor(options) {
        if (typeof window !== 'undefined') {
            this.InitViewer(options);
        }
        this.mouseHandlers = [];
        this.redisplayPauseLevel = 0;
        this.VisAtom = this.VisAtom.bind(this);
        this.AppAtom = this.AppAtom.bind(this);
        this.MapAtoms = this.MapAtoms.bind(this);
    }

    // "NON-VIRTUAL" METHODS - not expected to be defined by child classes
    // Setup
    /**
     * Mouse events are handed to `handler(target, event)`. One of them is not a DOM event: a
     * mousewheel zoom arrives as a CustomEvent of type 'zoom' whose `detail.delta` every backend
     * MUST report in the DOM's WheelEvent.deltaY convention -- NEGATIVE for wheel up and for a
     * trackpad pinch-out, positive for wheel down and pinch-in, whichever way the backend's own
     * camera happens to zoom. Consumers (display_mgr's Ctrl+wheel binding-site radius) read the
     * sign of that value and nothing else, so a backend that reports its camera movement instead
     * changes what the user's gesture does. (A backend may also raise 'zoom' for a gesture that
     * is not a wheel -- 3Dmol does for Shift+drag -- and then documents its own sign; see
     * Visualizer3Dmol.)
     */
    RegisterMouseHandler(handler) {
        this.mouseHandlers.push(handler);
    }

    /**
     * Take a function that expects BMaps atoms and return one that expects visualizer atoms.
     * The resulting function will be called internally by the visualizer on the visulizer atoms,
     * but will ultimately evaluate BMaps atoms.
     * @param {(Atom)=>bool} selectorFn
     * @returns {bool}
     */
    wrapAtomFn(selectorFn) {
        return (visAtom) => selectorFn(this.AppAtom(visAtom));
    }

    /**
     * Find a Residue object for an atom. This is necessary because the parent atomGroup of the atom
     * is probably the chain object, not the residue.
     * It may be preferable to have the parent atom group be the residue.
     * This is kind of strange for this to be here.
     * Workspace also has an `atomGroupsForAtom` function, which looks at all atom groups.
     * @param {Atom} atom
     * @returns {Residue}
     */
    /**
     * The secondary structure BMaps recorded for an atom's residue: 'helix', 'sheet', or null
     * (a loop, a non-protein atom, or no annotation).
     * @param {import('BMapsModel').Atom} atom
     * @returns {'helix'|'sheet'|null}
     */
    static getAtomSecondaryStructure(atom) {
        const residue = VisualizerInterface.getAtomProteinResidue(atom);
        const info = residue && residue.getSecondaryStructureInfo
            && residue.getSecondaryStructureInfo();
        return info && (info.type === 'helix' || info.type === 'sheet') ? info.type : null;
    }

    static getAtomProteinResidue(atom) {
        if (atom.hetflag) return null;
        let residue = atom.getAtomGroup();
        if (residue instanceof Residue) {
            // Doesn't have to do anything, already a residue
        } else if (residue instanceof Polymer) {
            residue = residue.getResidues().find((res) => res.hasAtom(atom));
            if (!residue) {
                console.log(`Failed to find a residue for ${getFullResidueId(atom)} in ${atom.getAtomGroup()}`);
            }
        } else {
            // Unrecognized type
            residue = null;
        }
        return residue;
    }

    // Display
    Redisplay() {
        if (this.redisplayPauseLevel === 0) {
            this.RedisplayViewer();
        }
    }

    // PauseRedisplay
    // This will stop the viewer from refreshing until it is unpaused.
    // You MUST ensure that it is unpaused!
    PauseRedisplay(paused) {
        if (paused) {
            this.redisplayPauseLevel++;
        } else {
            this.redisplayPauseLevel--;
        }

        if (this.redisplayPauseLevel < 0) {
            console.warn('Negative redisplay pause level');
            this.redisplayPauseLevel = 0;
        }
    }

    // "VIRTUAL" METHODS - can be overridden by child classes
    InitViewer(options) { }

    /**
     * Remove the atoms (and their bonds) the app added, so the scene can be rebuilt by
     * displayAll(). Surfaces, shapes, labels and temporary molecules are NOT removed - their
     * owners remove them (e.g. removeHotspots() in display_mgr.doRedrawScene) - which is what
     * Visualizer3Dmol does. Content the user created directly in the backend (for example
     * through a native command line, see RunNativeCommand) may be kept.
     */
    Reset() { }

    /**
     * Remove everything, including content created directly in the backend.
     * Called on the 'zapAll' event; defaults to Reset().
     */
    ZapAll() { this.Reset(); }

    /**
     * Live switching (Preferences -> 3D viewer, MainCanvas.switchVisualizer): this visualizer
     * stops being the active one. Remove EVERYTHING drawn (atoms, surfaces, shapes, labels,
     * temporary molecules, user content), stop drawing and hide the canvas and any overlays, but
     * keep the instance: switching back calls Activate() on it instead of constructing another
     * (some backends hold resources that cannot be released, e.g. window listeners, a wasm
     * runtime). The app rebuilds the scene from its model into the next visualizer.
     */
    Deactivate() { this.ZapAll(); }

    /** The counterpart of Deactivate(): show the canvas again and resume drawing (empty). */
    Activate() { }

    /**
     * Release this (inactive) visualizer for good: its canvas and overlays leave the page. Used
     * for a backend that failed to load, so that choosing it again starts afresh.
     */
    Dispose() { }

    MapAtoms(appAtom, visAtom) { }
    UnmapAtom(appAtom) { }
    UnmapAtoms(appAtoms) { }

    /**
     * Hook for backends that color cartoons per residue rather than per atom: called after
     * all per-atom colors for a scene have been applied. No-op by default.
     */
    refreshCartoonColors() { }

    /**
     * Capture the 3D canvas as an image.
     * @param {{type?: string, quality?: number, ray?: boolean, width?: number, scale?: number,
     *     dpi?: number, outline?: boolean, transparentBackground?: boolean}} [options]
     *     `type`/`quality` as for canvas.toBlob(). `ray` asks for a ray-traced render instead of
     *     the screen frame (only when SupportsRayTracedImage()), `width` pixels across or at
     *     `scale` x the canvas size, tagged with `dpi` as its resolution, optionally with outlines
     *     and a transparent background.
     * @returns {Promise<Blob|null>} null if the backend has nothing to capture
     */
    GetImageBlob(options) { return Promise.resolve(null); }

    /** @returns {boolean} whether GetImageBlob({ray: true, ...}) is available */
    SupportsRayTracedImage() { return false; }

    /**
     * What GetImageBlob({ray: true, ...}) would produce for these options, so the export dialog
     * can show the size before spending seconds on the render.
     * @param {{width?: number, scale?: number}|number} [request]
     * @returns {{width: number, height: number, scale: number}|null} null when the backend has no
     *     ray tracer or nothing to render yet
     */
    RayTracedImageSize(request) { return null; }

    /** @returns {boolean} whether this backend writes its own session file (SaveNativeSession) */
    SupportsNativeSessionExport() { return false; }

    /**
     * The scene as the backend's own session file, for opening in that program - NOT a BMaps
     * session: BMaps never reads one back (see UserActions.CaptureState for that).
     * @returns {Promise<{blob: Blob, extension: string, format: string}|null>} null when the
     *     backend has no session to write
     */
    SaveNativeSession() { return Promise.resolve(null); }

    /**
     * How protein ribbons are drawn (the `CartoonStyle` preference, "Cartoon style: PyMOL |
     * BMaps"): 'pymol' = PyMOL's own proportions, 'maestro' = BMaps' usual narrow ribbons (the
     * numbers come from Schrödinger Maestro). A backend maps the name to what it can do and
     * leaves the rest - 3Dmol draws BMaps' look under both; the caller redraws. No-op by default.
     * @param {string} name
     */
    SetCartoonStyle(name) { }

    /**
     * How atoms and ribbons are coloured by default (the `ColorStyle` preference, themes.js
     * ColorStyles): 'bmaps' = carbons by chain / compound and ribbons by chain, 'maestro' =
     * light-grey carbons and ribbons by secondary structure. Only the DEFAULT colours change: a
     * custom colour, selectivity colouring or a selection still wins. The caller redraws. No-op
     * by default.
     * @param {string} name
     */
    SetColorStyle(name) { }

    /**
     * The rep-set representations (style_manager RepNames) this viewer draws; Advanced styles does
     * not offer the others for it (plan B3 option B). Default: the per-atom ones - 3Dmol has no
     * per-atom surface / mesh / dots.
     * @returns {string[]}
     */
    SupportedRepSetReps() { return ['lines', 'sticks', 'spheres', 'cartoon', 'ribbon']; }

    /**
     * The rep-set representations whose transparency this viewer sets per group (a rep set's
     * `transparency`). Default: 3Dmol's - cartoon opacity is per chain, while sticks and spheres
     * have one opacity per model (3Dmol draws them opaque unless all atoms agree).
     * @returns {string[]}
     */
    PerGroupTransparentReps() { return ['cartoon']; }

    // Native command line (e.g. PyMOL commands). Backends without a command language keep
    // these defaults; UI that offers a command input should check SupportsNativeCommands().

    /** @returns {boolean} */
    SupportsNativeCommands() { return false; }

    /**
     * Execute one line of the backend's native command language.
     * @param {string} text
     * @returns {{ok: boolean, output: string[], error: string|null}}
     */
    RunNativeCommand(text) {
        return { ok: false, output: [], error: 'This visualizer has no command language.' };
    }

    /**
     * Completion candidates for a partial native command.
     * @param {string} text
     * @returns {string[]}
     */
    CompleteNativeCommand(text) { return []; }

    /**
     * What a command-line UI shows next to the input. A backend that supports native commands
     * publishes NativeCommandStateEvent (EventBroker) with this object whenever it changes.
     * @returns {{status: 'unavailable'|'loading'|'ready'|'failed', message: string|null,
     *            overlay: boolean, attribution?: string, trademark?: string,
     *            restrictions?: string[]}}  `trademark`: a footnote the attribution requires
     */
    NativeCommandState() {
        return { status: 'unavailable', message: null, overlay: false };
    }

    // "ABSTRACT" METHODS - must be overriden by child classes
    ResizeViewer() { abstractImplWarning('ResizeViewer'); }
    GetCameraPosition() { abstractImplWarning('GetCameraPosition'); }
    SetCameraPosition() { abstractImplWarning('SetCameraPosition'); }
    SetBackgroundColor() { abstractImplWarning('SetBackgroundColor'); }
    RedisplayViewer() { abstractImplWarning('RedisplayViewer'); }
    ZoomToAtoms() { abstractImplWarning('ZoomToAtoms'); }
    RotateView() { abstractImplWarning('RotateView'); }
    ZoomView() { abstractImplWarning('ZoomView'); }
    PanView() { abstractImplWarning('PanView'); }
    ResetCameraOrientation() { abstractImplWarning('ResetCameraOrientation'); }

    // Dealing with atom and molecule data
    VisAtom(bmapsAtom) { abstractImplWarning('VisAtom'); }
    AppAtom(myAtom) { abstractImplWarning('AppAtom'); }

    MakeAtom(atom) { abstractImplWarning('MakeAtom'); }
    MakeBond(bond) { abstractImplWarning('MakeBond'); }
    AddAtoms(atom, bonds, sheets, helices) { abstractImplWarning('AddAtoms'); }
    GetAtomByUniqueID(uniqueID) { abstractImplWarning('GetAtomByUniqueID'); }
    MakeTempMolecule(atoms, style, colorMap) { abstractImplWarning('MakeTempMolecule'); }
    RemoveTempMolecule(tempModel) { abstractImplWarning('RemoveTempMolecule'); }
    UpdateTempMolecule(tempModel, props) { abstractImplWarning('UpdateTempMolecule'); }
    RemoveAtoms(atoms) { abstractImplWarning('RemoveAtoms'); }

    // Style
    StyleAtom(atom, style) { abstractImplWarning('StyleAtom'); }
    StyleAtoms(style, selectorFn) { abstractImplWarning('StyleAtoms'); }
    setAtomSelected(atom, selected) { abstractImplWarning('setAtomSelected'); }
    setAtomColor(atom, color) { abstractImplWarning('setAtomColor'); }
    atomIsVisible(atom) { abstractImplWarning('atomIsVisible'); }

    // Objects
    DrawSurface() { abstractImplWarning('DrawSurface'); }
    RemoveSurface() { abstractImplWarning('RemoveSurface'); }
    RemoveShape() { abstractImplWarning('RemoveShape'); }

    // More objects: JLKjr suggests rolling these all into DrawShape() and RemoveShape()
    drawLine() { }
    removeLine() { }
    drawSphere() { }
    removeSphere() { }
    drawCylinder() { }
    removeCylinder() { }
    drawCone() { }
    removeCone() { }
    drawArrow() { }
    removeArrow() { }
    drawLabel() { }
    removeLabel() { }

    // Additional proposed methods
    // SelectAtoms() { abstractImplWarning("SelectAtoms"); }
    // DrawShape() { abstractImplWarning("DrawShape"); }
    // GetView() { abstractImplWarning("GetView"); }
    // SetView() { abstractImplWarning("SetView"); }
    // addProtein() { abstractImplWarning("addProtein"); }
    // addCompound() { abstractImplWarning("addCompound"); }
    // addFragment() { abstractImplWarning("addFragment"); }
    // styleCompound() { abstractImplWarning("styleCompound"); }
    // styleProtein() { abstractImplWarning("styleProtein"); }

    // "protected"
    publishMouseEvent(target, mouseEvent) {
        for (const handler of this.mouseHandlers) {
            handler(target, mouseEvent);
        }
    }
}

function abstractImplWarning(method) {
    console.warn(`Abstract method call on 3d visualizer : ${method}.`);
}
